/**
 * NavigatorX Well Data Manager
 * 
 * Dashboard for managing well data:
 * - Most favorited/navigated wells
 * - Search trends
 * - Data source status
 * - Well overrides and corrections
 * 
 * @author Claude AI for TGE.LA
 * @version 1.0.0
 */

import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import {
  MapPin,
  Star,
  Navigation,
  Search,
  Database,
  RefreshCw,
  Loader2,
  CheckCircle,
  AlertCircle,
  XCircle,
  Plus,
  Trash2,
  Clock,
  TrendingUp,
  BarChart3,
  Globe,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import { auth } from '../../../utils/firebase/client';
import {
  getWellDataStats,
  getWellOverrides,
  approveWellOverride,
  deleteWellOverride,
  blacklistWell,
  createCustomWell,
  type WellStats,
  type WellOverride,
  type WellDataStats,
  type DataSourceStatus
} from '../../../utils/services/navigatorXWellService';

interface WellDataManagerProps {
  embedded?: boolean;
}

export default function WellDataManager({ embedded = false }: WellDataManagerProps) {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<WellDataStats | null>(null);
  const [overrides, setOverrides] = useState<WellOverride[]>([]);
  const [activeTab, setActiveTab] = useState<'favorites' | 'navigations' | 'trends' | 'sources' | 'overrides'>('favorites');
  const [showAddOverride, setShowAddOverride] = useState(false);
  
  // Form state for new override
  const [newOverride, setNewOverride] = useState({
    wellId: '',
    type: 'blacklist' as 'blacklist' | 'correction' | 'custom',
    reason: '',
    field: '',
    originalValue: '',
    correctedValue: ''
  });

  const adminId = auth.currentUser?.uid || 'unknown';

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsData, overridesData] = await Promise.all([
        getWellDataStats(),
        getWellOverrides()
      ]);
      setStats(statsData);
      setOverrides(overridesData);
    } catch (error) {
      console.error('Error loading well data:', error);
      toast.error('Failed to load well data');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveOverride = async (overrideId: string) => {
    try {
      await approveWellOverride(overrideId, adminId);
      toast.success('Override approved');
      loadData();
    } catch (error: any) {
      toast.error(error.message || 'Failed to approve override');
    }
  };

  const handleDeleteOverride = async (overrideId: string) => {
    if (!confirm('Delete this override?')) return;
    try {
      await deleteWellOverride(overrideId);
      toast.success('Override deleted');
      loadData();
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete override');
    }
  };

  const handleAddOverride = async () => {
    if (!newOverride.wellId || !newOverride.reason) {
      toast.error('Well ID and reason are required');
      return;
    }

    try {
      if (newOverride.type === 'blacklist') {
        await blacklistWell(newOverride.wellId, newOverride.reason, adminId);
      } else if (newOverride.type === 'custom') {
        // For custom wells, parse the correctedValue as JSON
        const wellData = JSON.parse(newOverride.correctedValue);
        await createCustomWell({ ...wellData, wellId: newOverride.wellId }, adminId);
      }
      toast.success('Override created');
      setShowAddOverride(false);
      setNewOverride({ wellId: '', type: 'blacklist', reason: '', field: '', originalValue: '', correctedValue: '' });
      loadData();
    } catch (error: any) {
      toast.error(error.message || 'Failed to create override');
    }
  };

  const getSourceStatusColor = (status: string) => {
    switch (status) {
      case 'healthy': return 'bg-green-100 text-green-800';
      case 'degraded': return 'bg-yellow-100 text-yellow-800';
      case 'error': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getSourceStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy': return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'degraded': return <AlertCircle className="h-4 w-4 text-yellow-500" />;
      case 'error': return <XCircle className="h-4 w-4 text-red-500" />;
      default: return <Database className="h-4 w-4 text-gray-500" />;
    }
  };

  if (loading) {
    return (
      <div className={`flex items-center justify-center ${embedded ? 'py-12' : 'min-h-[400px]'}`}>
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className={embedded ? '' : 'p-6'}>
      {/* Header */}
      {!embedded && (
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Well Data Manager</h2>
            <p className="text-gray-500">Statistics, trends, and data management</p>
          </div>
          <Button variant="outline" onClick={loadData} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      )}

      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Total Favorites</p>
                <p className="text-2xl font-bold text-yellow-600">{stats?.totalFavorites || 0}</p>
                <p className="text-xs text-gray-400">{stats?.uniqueWellsFavorited || 0} unique wells</p>
              </div>
              <Star className="h-8 w-8 text-yellow-300" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Total Navigations</p>
                <p className="text-2xl font-bold text-blue-600">{stats?.totalNavigations || 0}</p>
                <p className="text-xs text-gray-400">{stats?.uniqueWellsNavigated || 0} unique wells</p>
              </div>
              <Navigation className="h-8 w-8 text-blue-300" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Search Terms</p>
                <p className="text-2xl font-bold text-gray-700">{stats?.recentSearches.length || 0}</p>
              </div>
              <Search className="h-8 w-8 text-gray-300" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Pending Overrides</p>
                <p className="text-2xl font-bold text-orange-600">
                  {overrides.filter(o => !o.approved).length}
                </p>
              </div>
              <AlertTriangle className="h-8 w-8 text-orange-300" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-4 border-b pb-2">
        {[
          { id: 'favorites', label: 'Top Favorites', icon: Star },
          { id: 'navigations', label: 'Top Navigations', icon: Navigation },
          { id: 'trends', label: 'Search Trends', icon: TrendingUp },
          { id: 'sources', label: 'Data Sources', icon: Database },
          { id: 'overrides', label: 'Overrides', icon: AlertTriangle }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'bg-blue-100 text-blue-700'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <Card>
        <CardContent className="p-4">
          {/* Top Favorited Wells */}
          {activeTab === 'favorites' && (
            <div className="space-y-2">
              {stats?.topFavoritedWells.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No favorited wells yet</p>
              ) : (
                stats?.topFavoritedWells.map((well, i) => (
                  <div key={well.wellId} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                        i === 0 ? 'bg-yellow-100 text-yellow-700' :
                        i === 1 ? 'bg-gray-100 text-gray-600' :
                        i === 2 ? 'bg-orange-100 text-orange-700' :
                        'bg-gray-50 text-gray-500'
                      }`}>
                        {i + 1}
                      </div>
                      <div>
                        <p className="font-medium">{well.wellName}</p>
                        <p className="text-xs text-gray-500">
                          {well.operator && `${well.operator} • `}
                          {well.county && well.state && `${well.county}, ${well.state}`}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className="flex items-center gap-1 text-yellow-600">
                          <Star className="h-4 w-4" />
                          <span className="font-medium">{well.favoriteCount}</span>
                        </div>
                        <p className="text-xs text-gray-400">{well.navigationCount} navs</p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Top Navigated Wells */}
          {activeTab === 'navigations' && (
            <div className="space-y-2">
              {stats?.topNavigatedWells.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No navigation history yet</p>
              ) : (
                stats?.topNavigatedWells.map((well, i) => (
                  <div key={well.wellId} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                        i === 0 ? 'bg-blue-100 text-blue-700' :
                        i === 1 ? 'bg-gray-100 text-gray-600' :
                        i === 2 ? 'bg-cyan-100 text-cyan-700' :
                        'bg-gray-50 text-gray-500'
                      }`}>
                        {i + 1}
                      </div>
                      <div>
                        <p className="font-medium">{well.wellName}</p>
                        <p className="text-xs text-gray-500">
                          {well.operator && `${well.operator} • `}
                          {well.county && well.state && `${well.county}, ${well.state}`}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className="flex items-center gap-1 text-blue-600">
                          <Navigation className="h-4 w-4" />
                          <span className="font-medium">{well.navigationCount}</span>
                        </div>
                        <p className="text-xs text-gray-400">{well.favoriteCount} favs</p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Search Trends */}
          {activeTab === 'trends' && (
            <div className="space-y-2">
              {stats?.recentSearches.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No search data yet</p>
              ) : (
                stats?.recentSearches.map((trend, i) => (
                  <div key={trend.term} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Search className="h-4 w-4 text-gray-400" />
                      <span className="font-medium">{trend.term}</span>
                    </div>
                    <Badge variant="outline">{trend.count} searches</Badge>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Data Sources */}
          {activeTab === 'sources' && (
            <div className="space-y-3">
              {stats?.dataSourceStatus.length === 0 ? (
                <div className="text-center py-8">
                  <Database className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No data sources configured</p>
                  <p className="text-sm text-gray-400">Data sources will appear here when synced</p>
                </div>
              ) : (
                stats?.dataSourceStatus.map(source => (
                  <div key={source.source} className="p-4 border rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {getSourceStatusIcon(source.status)}
                        <div>
                          <p className="font-medium">{source.source}</p>
                          <p className="text-xs text-gray-500">
                            {source.recordCount.toLocaleString()} records
                          </p>
                        </div>
                      </div>
                      <Badge className={getSourceStatusColor(source.status)}>
                        {source.status}
                      </Badge>
                    </div>
                    {source.lastError && (
                      <div className="mt-2 p-2 bg-red-50 rounded text-sm text-red-700">
                        {source.lastError}
                      </div>
                    )}
                    <div className="mt-2 text-xs text-gray-400">
                      Last sync: {source.lastSync?.toDate?.().toLocaleString() || 'Never'}
                      {source.syncDuration && ` (${source.syncDuration}ms)`}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Overrides */}
          {activeTab === 'overrides' && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <p className="text-sm text-gray-500">{overrides.length} total overrides</p>
                <Button size="sm" onClick={() => setShowAddOverride(true)}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Override
                </Button>
              </div>

              {/* Add Override Form */}
              {showAddOverride && (
                <div className="mb-4 p-4 border rounded-lg bg-gray-50">
                  <h4 className="font-medium mb-3">New Override</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Well ID</Label>
                      <Input
                        value={newOverride.wellId}
                        onChange={(e) => setNewOverride({ ...newOverride, wellId: e.target.value })}
                        placeholder="Enter well ID"
                      />
                    </div>
                    <div>
                      <Label>Type</Label>
                      <select
                        value={newOverride.type}
                        onChange={(e) => setNewOverride({ ...newOverride, type: e.target.value as any })}
                        className="w-full border rounded px-3 py-2"
                      >
                        <option value="blacklist">Blacklist</option>
                        <option value="correction">Correction</option>
                        <option value="custom">Custom Well</option>
                      </select>
                    </div>
                    <div className="col-span-2">
                      <Label>Reason</Label>
                      <Input
                        value={newOverride.reason}
                        onChange={(e) => setNewOverride({ ...newOverride, reason: e.target.value })}
                        placeholder="Reason for override"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <Button size="sm" onClick={handleAddOverride}>Create</Button>
                    <Button size="sm" variant="outline" onClick={() => setShowAddOverride(false)}>Cancel</Button>
                  </div>
                </div>
              )}

              {/* Override List */}
              <div className="space-y-2">
                {overrides.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">No overrides</p>
                ) : (
                  overrides.map(override => (
                    <div key={override.id} className={`p-3 border rounded-lg ${
                      override.approved ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'
                    }`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="capitalize">{override.type}</Badge>
                            <code className="text-sm bg-gray-100 px-2 py-1 rounded">{override.wellId}</code>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">{override.reason}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {override.approved ? (
                            <Badge className="bg-green-100 text-green-800">Approved</Badge>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleApproveOverride(override.id)}
                              className="text-green-600"
                            >
                              <CheckCircle className="h-4 w-4 mr-1" />
                              Approve
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeleteOverride(override.id)}
                            className="text-red-600"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
